import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';

export type AssetKey =
  | 'player'
  | 'clerk'
  | 'customer'
  | 'counter'
  | 'barn'
  | 'farmhouse'
  | 'silo'
  | 'greenhouse'
  | 'waterTower'
  | 'shelter'
  | 'beehive'
  | 'crate'
  | 'barrel'
  | 'treeApple'
  | 'treeLarge'
  | 'flowers1'
  | 'flowers2'
  | 'flowers3'
  | 'dirtRows'
  | 'vegeRows'
  | 'pebbles';

const FBX_MAP: Record<AssetKey, string> = {
  player: '/assets/synty-farm/characters/SK_Chr_FarmGirl_01.fbx',
  clerk: '/assets/synty-farm/characters/SK_Chr_Farmer_Male_01.fbx',
  customer: '/assets/synty-farm/characters/SK_Chr_Farmer_Female_01.fbx',
  counter: '/assets/synty-farm/fbx/SM_Bld_ProduceStand_01.fbx',
  barn: '/assets/synty-farm/fbx/SM_Bld_Barn_01.fbx',
  farmhouse: '/assets/synty-farm/fbx/SM_Bld_Farmhouse_01.fbx',
  silo: '/assets/synty-farm/fbx/SM_Bld_Silo_01.fbx',
  greenhouse: '/assets/synty-farm/fbx/SM_Bld_Greenhouse_01.fbx',
  waterTower: '/assets/synty-farm/fbx/SM_Bld_WaterTower_01.fbx',
  shelter: '/assets/synty-farm/fbx/SM_Bld_Shelter_01.fbx',
  beehive: '/assets/synty-farm/fbx/SM_Prop_Beehive_01.fbx',
  crate: '/assets/synty-farm/fbx/SM_Prop_Crate_01.fbx',
  barrel: '/assets/synty-farm/fbx/SM_Prop_Barrel_01.fbx',
  treeApple: '/assets/synty-farm/fbx/SM_Env_Tree_Apple_Grown_01.fbx',
  treeLarge: '/assets/synty-farm/fbx/SM_Env_Tree_Large_01.fbx',
  flowers1: '/assets/synty-farm/fbx/SM_Env_Flowers_01.fbx',
  flowers2: '/assets/synty-farm/fbx/SM_Env_Flowers_02.fbx',
  flowers3: '/assets/synty-farm/fbx/SM_Env_Flowers_03.fbx',
  dirtRows: '/assets/synty-farm/fbx/SM_Env_Dirt_Rows_01.fbx',
  vegeRows: '/assets/synty-farm/fbx/SM_Env_Vege_Rows_01.fbx',
  pebbles: '/assets/synty-farm/fbx/SM_Env_Pebbles_01.fbx',
};

/** Synty POLYGON units are cm-scale → meters for our world. */
const DEFAULT_SCALE: Partial<Record<AssetKey, number>> = {
  player: 0.012,
  clerk: 0.012,
  customer: 0.012,
  counter: 0.014,
  barn: 0.012,
  farmhouse: 0.012,
  silo: 0.012,
  greenhouse: 0.012,
  waterTower: 0.012,
  shelter: 0.012,
  beehive: 0.014,
  crate: 0.014,
  barrel: 0.014,
  treeApple: 0.012,
  treeLarge: 0.011,
  flowers1: 0.014,
  flowers2: 0.014,
  flowers3: 0.014,
  dirtRows: 0.014,
  vegeRows: 0.014,
  pebbles: 0.014,
};

export class AssetLibrary {
  private readonly templates = new Map<AssetKey, THREE.Object3D>();
  private atlas: THREE.Texture | null = null;
  private ready = false;

  get isReady(): boolean {
    return this.ready;
  }

  get hasAssets(): boolean {
    return this.ready && this.templates.size > 0;
  }

  async load(): Promise<void> {
    try {
      const texLoader = new THREE.TextureLoader();
      // Primary color atlas used across POLYGON Farm
      this.atlas = await texLoader.loadAsync('/assets/synty-farm/textures/PolygonFarm_Texture_01_A.png');
      this.atlas.colorSpace = THREE.SRGBColorSpace;
      this.atlas.anisotropy = 8;
      this.atlas.wrapS = this.atlas.wrapT = THREE.RepeatWrapping;

      const fbx = new FBXLoader();
      const keys = Object.keys(FBX_MAP) as AssetKey[];
      await Promise.all(
        keys.map(async (key) => {
          try {
            const obj = await fbx.loadAsync(FBX_MAP[key]);
            this.prepareTemplate(key, obj);
            this.templates.set(key, obj);
          } catch (err) {
            console.warn(`[assets] failed ${key}`, err);
          }
        }),
      );
      this.ready = this.templates.size > 0;
    } catch (err) {
      console.warn('[assets] library load failed, using procedural fallbacks', err);
      this.ready = false;
    }
  }

  clone(key: AssetKey, scale?: number): THREE.Group | null {
    const tpl = this.templates.get(key);
    if (!tpl) return null;
    const g = new THREE.Group();
    const mesh = tpl.clone(true);
    const s = scale ?? DEFAULT_SCALE[key] ?? 0.012;
    mesh.scale.setScalar(s);
    mesh.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(mesh);
    if (Number.isFinite(box.min.y)) mesh.position.y -= box.min.y;
    g.add(mesh);
    g.userData.assetKey = key;
    return g;
  }

  private prepareTemplate(key: AssetKey, root: THREE.Object3D): void {
    root.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.castShadow = true;
      child.receiveShadow = true;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      const next = mats.map((m) => this.upgradeMaterial(m, key));
      child.material = Array.isArray(child.material) ? next : next[0]!;
    });
    // Strip lights/cameras if any
    const remove: THREE.Object3D[] = [];
    root.traverse((c) => {
      if ((c as THREE.Light).isLight || (c as THREE.Camera).isCamera) remove.push(c);
    });
    for (const r of remove) r.parent?.remove(r);
  }

  private upgradeMaterial(mat: THREE.Material, key: AssetKey): THREE.Material {
    const atlas = this.atlas;
    if (mat instanceof THREE.MeshStandardMaterial || mat instanceof THREE.MeshPhongMaterial || mat instanceof THREE.MeshLambertMaterial) {
      const std = new THREE.MeshStandardMaterial({
        map: atlas ?? (mat as THREE.MeshStandardMaterial).map ?? null,
        color: atlas ? 0xffffff : (mat as THREE.MeshStandardMaterial).color?.getHex?.() ?? 0xcccccc,
        roughness: key.includes('tree') || key.includes('flower') ? 0.85 : 0.62,
        metalness: 0.05,
        flatShading: false,
        side: THREE.FrontSide,
      });
      // Keep original map if present and better
      const srcMap = (mat as THREE.MeshStandardMaterial).map;
      if (srcMap) {
        srcMap.colorSpace = THREE.SRGBColorSpace;
        std.map = srcMap;
      } else if (atlas) {
        std.map = atlas;
      }
      std.envMapIntensity = 0.55;
      return std;
    }
    return mat;
  }
}

export function createProceduralSkyTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 512;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, 512);
  g.addColorStop(0, '#5eb7ef');
  g.addColorStop(0.45, '#9ad4f5');
  g.addColorStop(0.72, '#f2d2a8');
  g.addColorStop(1, '#f7e7c4');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 1024, 512);
  // soft clouds
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  for (let i = 0; i < 18; i++) {
    const x = (i * 137) % 1024;
    const y = 60 + (i * 53) % 180;
    const r = 40 + (i % 5) * 18;
    ctx.beginPath();
    ctx.ellipse(x, y, r * 1.6, r * 0.55, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.mapping = THREE.EquirectangularReflectionMapping;
  return tex;
}

export function createProceduralGrassTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#4fa86a';
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 900; i++) {
    const x = Math.random() * 256;
    const y = Math.random() * 256;
    const shade = 70 + Math.floor(Math.random() * 50);
    ctx.fillStyle = `rgb(${shade * 0.45},${shade},${shade * 0.55})`;
    ctx.fillRect(x, y, 2 + Math.random() * 3, 2 + Math.random() * 3);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(18, 18);
  tex.anisotropy = 4;
  return tex;
}
